/**
 * Claims Management System
 * Handles claim submission, listing, and details viewing
 */

// API Configuration
const CLAIMS_API_URL = 'https://app.roveconcepts.com/api/order-claims';

/**
 * Get store identifier from shop metafield
 */
function getStoreIdentifier() {
  if (window.storeConfig && window.storeConfig.prefix) {
    console.log('Store identifier from metafield:', window.storeConfig.prefix);
    return window.storeConfig.prefix;
  }
  
  console.warn('No store identifier found, defaulting to RCUS');
  return 'RCUS';
}


const STORE_IDENTIFIER = getStoreIdentifier();

// Global State
let customerData = null;
let customerOrders = [];
let customerClaims = [];
let selectedOrderId = null;
let uploadedImages = [];
let uploadedVideos = [];
let imageInputCounter = 1;
let videoInputCounter = 1;
let paginationData = {
  currentPage: 1,
  totalPages: 1,
  perPage: 15,
  total: 0
};

// Initialize on page load
document.addEventListener('DOMContentLoaded', function() {
  initializeClaims();
  // Wire up create/hide actions for sliding panel
  const layout = document.querySelector('.claims-layout');
  const createBtn = document.getElementById('create-claim-btn');
  if (createBtn && layout) {
    createBtn.addEventListener('click', () => showClaimForm());
  }
});

/**
 * Initialize the claims page
 */
function initializeClaims() {
  // Get customer data from hidden script tag
  const customerDataElement = document.getElementById('customer-data');
  if (customerDataElement) {
    customerData = JSON.parse(customerDataElement.textContent);
    
    // Load claims and orders
    loadClaims();
    loadOrders();
  }
}

/**
 * Sliding panel controls
 */
function showClaimForm() {
  const layout = document.querySelector('.claims-layout');
  const panel = document.getElementById('claim-form-panel');
  const formSection = document.getElementById('claim-form-section');
  if (layout && panel) {
    layout.classList.add('form-open');
    panel.setAttribute('aria-hidden', 'false');
  }
  if (formSection) formSection.style.display = '';
  // Reset form state when opening
  if (typeof resetClaimForm === 'function') {
    resetClaimForm();
  }
}

function hideClaimForm() {
  const layout = document.querySelector('.claims-layout');
  const panel = document.getElementById('claim-form-panel');
  if (layout && panel) {
    layout.classList.remove('form-open');
    panel.setAttribute('aria-hidden', 'true');
  }
}

/**
 * Load customer claims from API
 */
async function loadClaims(page = 1) {
  const loadingEl = document.getElementById('claims-loading');
  const errorEl = document.getElementById('claims-error');
  const emptyEl = document.getElementById('claims-empty');
  const tableEl = document.getElementById('claims-table-container');

  // Show loading state
  loadingEl.style.display = 'block';
  errorEl.style.display = 'none';
  emptyEl.style.display = 'none';
  tableEl.style.display = 'none';

  try {
    const response = await fetch(
      `${CLAIMS_API_URL}?customer_id=${encodeURIComponent(customerData.id)}&page=${page}`
    );

    if (!response.ok) {
      throw new Error('Failed to fetch claims');
    }

    const result = await response.json();
    
    loadingEl.style.display = 'none';

    if (result.success && result.data && result.data.length > 0) {
      customerClaims = result.data;

      console.log(customerClaims);
      
      // Update pagination data from API response
      if (result.pagination) {
        paginationData = {
          currentPage: result.pagination.current_page,
          totalPages: result.pagination.last_page,
          perPage: result.pagination.per_page,
          total: result.pagination.total
        };
      }
      
      displayClaims(customerClaims);
      renderPagination();
      tableEl.style.display = 'block';
    } else {
      emptyEl.style.display = 'block';
      document.getElementById('claims-pagination').style.display = 'none';
    }
  } catch (error) {
    console.error('Error loading claims:', error);
    loadingEl.style.display = 'none';
    errorEl.style.display = 'block';
  }
}

/**
 * Display claims in the table
 */
function displayClaims(claims) {
  const tbody = document.getElementById('claims-tbody');
  tbody.innerHTML = '';

  claims.forEach(claim => {
    const row = document.createElement('tr');
    row.className = 'claims-table-row';
    row.style.cursor = 'pointer';
    row.onclick = () => showClaimDetails(claim.claim_number);
    
    // Format products list from items array (product name + qty)
    const products = claim.items && claim.items.length > 0
      ? claim.items.map(item => {
          const explicitTitle = item.title || item.product_title || item.product_name || item.name;
          return `${getProductTitle(item.product_id, item.sku, explicitTitle)} (${item.quantity})`;
        }).join(', ')
      : 'N/A';
    
    // Format dates
    const createdDate = formatDate(claim.created_at);
    const updatedDate = formatDate(claim.updated_at);
    
    // Get status badge
    const statusBadge = getStatusBadge(claim.status);

    // Resolve order name from loaded customer orders (e.g. "#1001"), fallback to order_id
    const orderDisplay = (customerOrders && customerOrders.length && claim.order_id)
      ? (customerOrders.find(o => o.id == claim.order_id)?.name || claim.order_id)
      : (claim.order_id || 'N/A');
    
    row.innerHTML = `
      <td data-label="Claim ID">${claim.claim_number || claim.id}</td>
      <td data-label="Order ID" data-order-id="${claim.order_name}">${orderDisplay}</td>
      <td data-label="Product(s)">${products}</td>
      <td data-label="Status">${statusBadge}</td>
      <td data-label="Created">${createdDate}</td>
      <td data-label="Last Updated">${updatedDate}</td>
    `;
    
    tbody.appendChild(row);
  });
}

/**
 * Render pagination controls
 */
function renderPagination() {
  const paginationEl = document.getElementById('claims-pagination');
  if (!paginationEl) return;
  
  const { currentPage, totalPages, total } = paginationData;
  
  // Hide pagination if only one page
  if (totalPages <= 1) {
    paginationEl.style.display = 'none';
    return;
  }
  
  paginationEl.style.display = 'flex';
  
  let paginationHTML = '<div class="pagination-controls">';
  
  // Previous button
  if (currentPage > 1) {
    paginationHTML += `<button class="pagination-btn" onclick="goToPage(${currentPage - 1})" aria-label="Previous page">&laquo; Previous</button>`;
  } else {
    paginationHTML += `<button class="pagination-btn" disabled>&laquo; Previous</button>`;
  }
  
  // Page numbers
  paginationHTML += '<div class="pagination-pages">';
  
  const maxVisible = 5;
  let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
  let endPage = Math.min(totalPages, startPage + maxVisible - 1);
  
  if (endPage - startPage < maxVisible - 1) {
    startPage = Math.max(1, endPage - maxVisible + 1);
  }
  
  if (startPage > 1) {
    paginationHTML += `<button class="pagination-btn pagination-page" onclick="goToPage(1)">1</button>`;
    if (startPage > 2) {
      paginationHTML += `<span class="pagination-ellipsis">...</span>`;
    }
  }
  
  for (let i = startPage; i <= endPage; i++) {
    const activeClass = i === currentPage ? ' active' : '';
    paginationHTML += `<button class="pagination-btn pagination-page${activeClass}" onclick="goToPage(${i})">${i}</button>`;
  }
  
  if (endPage < totalPages) {
    if (endPage < totalPages - 1) {
      paginationHTML += `<span class="pagination-ellipsis">...</span>`;
    }
    paginationHTML += `<button class="pagination-btn pagination-page" onclick="goToPage(${totalPages})">${totalPages}</button>`;
  }
  
  paginationHTML += '</div>';
  
  // Next button
  if (currentPage < totalPages) {
    paginationHTML += `<button class="pagination-btn" onclick="goToPage(${currentPage + 1})" aria-label="Next page">Next &raquo;</button>`;
  } else {
    paginationHTML += `<button class="pagination-btn" disabled>Next &raquo;</button>`;
  }
  
  paginationHTML += '</div>';
  paginationHTML += `<div class="pagination-info">Page ${currentPage} of ${totalPages} (${total} total claims)</div>`;
  
  paginationEl.innerHTML = paginationHTML;
}

/**
 * Navigate to a specific page
 */
function goToPage(page) {
  if (page < 1 || page > paginationData.totalPages) return;
  loadClaims(page);
  // Scroll to top of claims list
  document.getElementById('claims-list-section').scrollIntoView({ behavior: 'smooth', block: 'start' });
}

/**
 * Format date for display
 */
function formatDate(dateString) {
  if (!dateString) return 'N/A';
  
  const date = new Date(dateString);
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  
  return `${month}/${day}/${year} - ${hours}:${minutes}`;
}

/**
 * Load customer orders from Shopify
 */
async function loadOrders() {
  const ordersContainer = document.getElementById('order-list');
  const loadingEl = document.getElementById('orders-loading');
  
  try {
    const orders = window.customerOrders || [];
    const fulfilledOrders = orders.filter(order => (order.fulfillment_status || '').toLowerCase() === 'fulfilled');
    
    if (fulfilledOrders.length === 0) {
      loadingEl.innerHTML = '<p>No fulfilled orders found.</p>';
      return;
    }
    
    customerOrders = fulfilledOrders;
    console.log(fulfilledOrders);
    displayOrders(fulfilledOrders);

    // Claims may render before orders load; refresh the table so the Order column shows order names.
    if (customerClaims && customerClaims.length) {
      displayClaims(customerClaims);
    }
  } catch (error) {
    console.error('Error loading orders:', error);
    loadingEl.innerHTML = '<p>Error loading orders. Please refresh the page.</p>';
  }
}

/**
 * Display orders in form
 */
function displayOrders(orders) {
  const ordersContainer = document.getElementById('order-list');
  ordersContainer.innerHTML = '';

  // Defensive: ensure we only render fulfilled orders
  orders = orders.filter(order => (order.fulfillment_status || '').toLowerCase() === 'fulfilled');

  orders.forEach(order => {
    const orderDate = formatOrderDate(order.created_at);
    const orderStatus = order.fulfillment_status || 'Pending';
    
    const orderItem = document.createElement('label');
    orderItem.className = 'order-item order-item--single-line';
    orderItem.innerHTML = `
      <input type="radio" name="order_id" value="${order.id}" onchange="selectOrder('${order.id}')">
      <strong>${order.name}</strong> - (${orderStatus}) ${orderDate}
    `;
    
    ordersContainer.appendChild(orderItem);
  });
}

/**
 * Format order date
 */
function formatOrderDate(dateString) {
  const date = new Date(dateString);
  const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
  return date.toLocaleDateString('en-US', options);
}

/**
 * Handle order selection
 */
function selectOrder(orderId) {
  selectedOrderId = orderId;
  
  // Find the selected order
  const order = customerOrders.find(o => o.id === orderId);
  
  if (order && order.line_items) {
    displayProducts(order.line_items);
    
    // Show product selection and subsequent fields
    document.getElementById('product-selection-group').style.display = 'block';
    document.getElementById('issue-description-group').style.display = 'block';
    document.getElementById('ideal-resolution-group').style.display = 'block';
    document.getElementById('customer-request-group').style.display = 'block';
    document.getElementById('image-upload-group').style.display = 'block';
    document.getElementById('video-upload-group').style.display = 'block';
    
    const adminGroup = document.getElementById('admin-management-group');
    if (adminGroup) adminGroup.style.display = 'block';
    
    const formActions = document.getElementById('form-actions');
    if (formActions) formActions.style.display = 'block';
  }
}

/**
 * Display products from selected order
 */
function displayProducts(lineItems) {
  const productList = document.getElementById('product-list');
  productList.innerHTML = '';
  ensureProductFormsContainer();
  const formsContainer = document.getElementById('product-forms');
  if (formsContainer) formsContainer.innerHTML = '';
  
  lineItems.forEach((item, index) => {
    console.log(`Product ${index}:`, item); // Debug: check if SKU exists
    const productItem = document.createElement('label');
    productItem.className = 'product-item';
    productItem.innerHTML = `
      <input type="checkbox" 
             name="products[]" 
             value="${index}"
             data-product-id="${item.product_id}"
             data-variant-id="${item.variant_id}"
             data-quantity="${item.quantity}"
             data-sku="${item.sku || ''}"
             data-title="${item.title}">
      <span class="product-item-label">${item.title} (x${item.quantity})</span>
    `;

    // Wire up checkbox to toggle its form
    const checkbox = productItem.querySelector('input[type="checkbox"]');
    checkbox.addEventListener('change', () => toggleProductForm(item, index, checkbox.checked));
    
    productList.appendChild(productItem);
  });
}

/**
 * Ensure a container exists for product-specific forms
 */
function ensureProductFormsContainer() {
  let formsContainer = document.getElementById('product-forms');
  if (!formsContainer) {
    const group = document.getElementById('product-selection-group');
    formsContainer = document.createElement('div');
    formsContainer.id = 'product-forms';
    formsContainer.style.marginTop = '1rem';
    group && group.appendChild(formsContainer);
  }
}

/**
 * Toggle creation/removal of per-product form
 */
function toggleProductForm(item, index, checked) {
  ensureProductFormsContainer();
  const formsContainer = document.getElementById('product-forms');
  const formId = `product-form-${index}`;
  let formEl = document.getElementById(formId);
  if (checked) {
    if (!formEl) {
      formEl = renderProductForm(item, index);
      formsContainer.appendChild(formEl);
    }
  } else if (formEl) {
    formEl.remove();
  }
}

/**
 * Render a product-specific form block
 */
function renderProductForm(item, index) {
  const formEl = document.createElement('div');
  formEl.className = 'product-incident-form';
  formEl.id = `product-form-${index}`;

  const qtyOptions = Array.from({ length: item.quantity }, (_, i) => `<option value="${i+1}">${i+1}</option>`).join('');
  const natureOptions = [
    'Order Error',
    'Product Defect',
    'Shipping Damage',
    'Warranty',
    'Lost in Transit',
    'Missing Parts'
  ].map(n => `<option value="${n}">${n}</option>`).join('');

  const defectSubCategories = [
    'Assembly / Fit Issue',
    'Cloudy Finish',
    'Consistent Shipping Damage',
    'Discoloration',
    'Failure After Normal Use',
    'Finish Quality Poor',
    'Feather Loss',
    'Gapping',
    'Labeling Error',
    'Mismatched Across Order / Within Same Order',
    'Missing or Incorrect Parts',
    'Missing or Misaligned Holes',
    'Not as Advertised',
    'Packaging Damage',
    'Premature Degradation',
    'Sagging',
    'Sinking',
    'Stitching Issues',
    'Stripped Threading',
    'Uneven Finish / Surface',
    'Visible Glue',
    'Warping',
    'Wobbly / Unstable'
  ];
  const defectOptions = defectSubCategories.map(d => `<option value="${d}">${d}</option>`).join('');

  formEl.innerHTML = `
    <div class="product-incident-title">${item.title}</div>
    <div class="product-incident-subtitle">Please complete the following information relating to the incident of this item.</div>
    <div class="product-incident-grid">
      <div class="product-incident-field">
        <label class="product-incident-label">Quantity</label>
        <select name="item_qty_${index}" class="incident-qty product-incident-input" data-index="${index}">${qtyOptions}</select>
      </div>
      <div class="product-incident-field">
        <label class="product-incident-label">Nature *</label>
        <select name="item_nature_${index}" class="incident-nature product-incident-input" data-index="${index}">${natureOptions}</select>
      </div>
      <div class="product-incident-field">
        <label class="product-incident-label">PI Label No.</label>
        <input type="text" name="pi_label_${index}" class="incident-pi product-incident-input" data-index="${index}" />
      </div>
      <div class="product-incident-field">
        <label class="product-incident-label">QC Sticker No.</label>
        <input type="text" name="qc_sticker_${index}" class="incident-qc product-incident-input" data-index="${index}" />
      </div>
      <div class="product-incident-field">
        <label class="product-incident-label">&nbsp;</label>
        <button type="button" class="product-incident-help" onclick="showStickerInfoModal()">Where I can find this data?</button>
      </div>
    </div>
  `;
  return formEl;
}

/**
 * Toggle defect sub-category dropdown
 */
function toggleDefectSubCategory(index, natureValue) {
  const defectField = document.getElementById(`defect-subcategory-${index}`);
  if (defectField) {
    defectField.style.display = (natureValue === 'Product Defect') ? 'block' : 'none';
  }
}

/**
 * Show sticker info modal with image
 */
function showStickerInfoModal() {
  const existing = document.getElementById('sticker-info-modal');
  if (existing) {
    existing.style.display = 'flex';
    return;
  }
  const modal = document.createElement('div');
  modal.className = 'claim-modal sticker-info-modal';
  modal.id = 'sticker-info-modal';
  modal.innerHTML = `
    <div class="claim-modal-overlay" onclick="closeStickerInfoModal()"></div>
    <div class="claim-modal-content">
      <div class="claim-modal-header">
        <h2>Where to find QC Sticker</h2>
        <button class="claim-modal-close" onclick="closeStickerInfoModal()">&times;</button>
      </div>
      <div class="claim-modal-body sticker-info-body">
        <img src="https://cdn.shopify.com/s/files/1/0683/4074/4392/files/sticker-info-2024.png?v=1764742372" alt="Sticker Information" class="sticker-info-image" />
      </div>
    </div>`;
  document.body.appendChild(modal);
}

function closeStickerInfoModal() {
  const modal = document.getElementById('sticker-info-modal');
  if (modal) modal.style.display = 'none';
}

/**
 * Show claim form
 */
// Deprecated handlers replaced by sliding panel controls above

/**
 * Reset claim form
 */
function resetClaimForm() {
  document.getElementById('claim-form').reset();
  selectedOrderId = null;
  uploadedImages = [];
  uploadedVideos = [];
  imageInputCounter = 1;
  videoInputCounter = 1;
  
  // Hide conditional fields
  document.getElementById('product-selection-group').style.display = 'none';
  document.getElementById('issue-description-group').style.display = 'none';
  document.getElementById('ideal-resolution-group').style.display = 'none';
  document.getElementById('customer-request-group').style.display = 'none';
  document.getElementById('image-upload-group').style.display = 'none';
  document.getElementById('video-upload-group').style.display = 'none';
  // Don't hide form-actions - submit button should always be visible
  // document.getElementById('form-actions').style.display = 'none';
  
  // Clear previews (new multiple selection inputs)
  const imagePreviews = document.getElementById('image-previews');
  const videoPreviews = document.getElementById('video-previews');
  if (imagePreviews) imagePreviews.innerHTML = '';
  if (videoPreviews) videoPreviews.innerHTML = '';
  const imageInput = document.getElementById('image-input');
  const videoInput = document.getElementById('video-input');
  if (imageInput) imageInput.value = '';
  if (videoInput) videoInput.value = '';
  
  // Clear errors
  document.querySelectorAll('.field-error').forEach(el => el.textContent = '');
}

/**
 * Handle multiple image selection
 */
function handleImageSelection(event) {
  const files = Array.from(event.target.files);
  
  files.forEach(file => {
    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      alert(`Image file ${file.name} is too large. Maximum size is 10MB`);
      return;
    }
    
    // Validate file type
    if (!file.type.match('image/(jpeg|jpg|png)')) {
      alert(`File ${file.name} is not a valid image. Only JPG and PNG are allowed`);
      return;
    }
    
    // Add to uploaded images
    uploadedImages.push(file);
    
    // Show preview
    showImagePreview(file);
  });
  
  // Reset input so same files can be selected again
  event.target.value = '';
}

/**
 * Handle multiple video selection
 */
function handleVideoSelection(event) {
  const files = Array.from(event.target.files);
  
  files.forEach(file => {
    // Validate file size (max 50MB)
    if (file.size > 50 * 1024 * 1024) {
      alert(`Video file ${file.name} is too large. Maximum size is 50MB`);
      return;
    }
    
    // Validate file type
    if (!file.type.match('video/(mp4|mov|avi)')) {
      alert(`File ${file.name} is not a valid video. Only MP4, MOV, and AVI are allowed`);
      return;
    }
    
    // Add to uploaded videos
    uploadedVideos.push(file);
    
    // Show preview
    showVideoPreview(file);
  });
  
  // Reset input so same files can be selected again
  event.target.value = '';
}

/**
 * Show image preview with thumbnail
 */
function showImagePreview(file) {
  const previewsContainer = document.getElementById('image-previews');
  const reader = new FileReader();
  
  reader.onload = function(e) {
    const index = uploadedImages.length - 1;
    const previewItem = document.createElement('div');
    previewItem.className = 'preview-item';
    previewItem.innerHTML = `
      <img src="${e.target.result}" alt="${file.name}">
      <button type="button" class="preview-item-remove" onclick="removeImage(${index})" title="Remove">&times;</button>
      <div class="preview-item-name">${file.name}</div>
    `;
    previewsContainer.appendChild(previewItem);
  };
  
  reader.readAsDataURL(file);
}

/**
 * Show video preview with thumbnail
 */
function showVideoPreview(file) {
  const previewsContainer = document.getElementById('video-previews');
  const reader = new FileReader();
  
  reader.onload = function(e) {
    const index = uploadedVideos.length - 1;
    const previewItem = document.createElement('div');
    previewItem.className = 'preview-item';
    previewItem.innerHTML = `
      <video src="${e.target.result}" muted></video>
      <button type="button" class="preview-item-remove" onclick="removeVideo(${index})" title="Remove">&times;</button>
      <div class="preview-item-name">${file.name}</div>
    `;
    previewsContainer.appendChild(previewItem);
  };
  
  reader.readAsDataURL(file);
}

/**
 * Remove image from upload list
 */
function removeImage(index) {
  uploadedImages.splice(index, 1);
  refreshImagePreviews();
}

/**
 * Remove video from upload list
 */
function removeVideo(index) {
  uploadedVideos.splice(index, 1);
  refreshVideoPreviews();
}

/**
 * Refresh image previews after removal
 */
function refreshImagePreviews() {
  const previewsContainer = document.getElementById('image-previews');
  previewsContainer.innerHTML = '';
  
  uploadedImages.forEach((file, index) => {
    const reader = new FileReader();
    reader.onload = function(e) {
      const previewItem = document.createElement('div');
      previewItem.className = 'preview-item';
      previewItem.innerHTML = `
        <img src="${e.target.result}" alt="${file.name}">
        <button type="button" class="preview-item-remove" onclick="removeImage(${index})" title="Remove">&times;</button>
        <div class="preview-item-name">${file.name}</div>
      `;
      previewsContainer.appendChild(previewItem);
    };
    reader.readAsDataURL(file);
  });
}

/**
 * Refresh video previews after removal
 */
function refreshVideoPreviews() {
  const previewsContainer = document.getElementById('video-previews');
  previewsContainer.innerHTML = '';
  
  uploadedVideos.forEach((file, index) => {
    const reader = new FileReader();
    reader.onload = function(e) {
      const previewItem = document.createElement('div');
      previewItem.className = 'preview-item';
      previewItem.innerHTML = `
        <video src="${e.target.result}" muted></video>
        <button type="button" class="preview-item-remove" onclick="removeVideo(${index})" title="Remove">&times;</button>
        <div class="preview-item-name">${file.name}</div>
      `;
      previewsContainer.appendChild(previewItem);
    };
    reader.readAsDataURL(file);
  });
}

/**
 * Handle form submission
 */
document.addEventListener('DOMContentLoaded', function() {
  const form = document.getElementById('claim-form');
  if (form) {
    form.addEventListener('submit', async function(e) {
      e.preventDefault();
      
      // Validate form
      if (!validateClaimForm()) {
        return;
      }
      
      // Disable submit button
      const submitBtn = document.getElementById('submit-claim-btn');
      submitBtn.disabled = true;
      submitBtn.textContent = 'SUBMITTING...';
      
      try {
        await submitClaim();
      } catch (error) {
        console.error('Error submitting claim:', error);
        showDialog('Error', error.message || 'Failed to submit claim. Please try again.', 'error');
        submitBtn.disabled = false;
        submitBtn.textContent = 'SUBMIT CLAIM';
      }
    });
  }
});

/**
 * Validate claim form
 */
function validateClaimForm() {
  let isValid = true;
  
  // Clear previous errors
  document.querySelectorAll('.field-error').forEach(el => el.textContent = '');
  
  // Validate order selection
  if (!selectedOrderId) {
    showError('order-error', 'Please select an order');
    isValid = false;
  }
  
  // Validate product selection
  const selectedProducts = document.querySelectorAll('input[name="products[]"]:checked');
  if (selectedProducts.length === 0) {
    showError('products-error', 'Please select at least one product');
    isValid = false;
  }
  
  // Validate issue description
  const issueDescription = document.getElementById('issue-description').value.trim();
  if (!issueDescription) {
    showError('issue-description-error', 'Please describe the issue');
    isValid = false;
  }
  
  // Validate ideal resolution
  const idealResolution = document.querySelector('input[name="ideal_resolution"]:checked');
  if (!idealResolution) {
    showError('ideal-resolution-error', 'Please select your ideal resolution');
    isValid = false;
  }
  
  // Validate customer request
  const customerRequest = document.getElementById('customer-request').value.trim();
  if (!customerRequest) {
    showError('customer-request-error', 'Please provide your request');
    isValid = false;
  }
  
  return isValid;
}

/**
 * Show error message
 */
function showError(elementId, message) {
  const errorEl = document.getElementById(elementId);
  if (errorEl) {
    errorEl.textContent = message;
  }
}

/**
 * Submit claim to API
 */
async function submitClaim() {
  const formData = new FormData();
  
  // Add basic fields
  formData.append('order_id', selectedOrderId);
  formData.append('store', STORE_IDENTIFIER);
  formData.append('customer_name', customerData.name);
  formData.append('customer_email', customerData.email);
  formData.append('customer_id', customerData.id);
  formData.append('issue_description', document.getElementById('issue-description').value.trim());
  formData.append('ideal_resolution', document.querySelector('input[name="ideal_resolution"]:checked').value);
  formData.append('customer_request', document.getElementById('customer-request').value.trim());
  
  // Add selected products using indexed bracket notation per API spec
  const selectedProducts = document.querySelectorAll('input[name="products[]"]:checked');
  let itemIndex = 0;
  selectedProducts.forEach(checkbox => {
    const index = parseInt(checkbox.value, 10);
    const qtyEl = document.querySelector(`select[name="item_qty_${index}"]`);
    const natureEl = document.querySelector(`select[name="item_nature_${index}"]`);
    const piEl = document.querySelector(`input[name="pi_label_${index}"]`);
    const qcEl = document.querySelector(`input[name="qc_sticker_${index}"]`);

    const qty = qtyEl ? parseInt(qtyEl.value, 10) : parseInt(checkbox.dataset.quantity, 10);
    const nature = natureEl ? natureEl.value : '';
    const pi = piEl ? piEl.value.trim() : '';
    const qc = qcEl ? qcEl.value.trim() : '';
    
    // Use indexed array notation so backend groups each item's fields together
    formData.append(`items[${itemIndex}][order_id]`, selectedOrderId);
    formData.append(`items[${itemIndex}][product_id]`, checkbox.dataset.productId || '');
    formData.append(`items[${itemIndex}][variant_id]`, checkbox.dataset.variantId || '');
    formData.append(`items[${itemIndex}][sku]`, checkbox.dataset.sku || '');
    formData.append(`items[${itemIndex}][quantity]`, String(qty));
    formData.append(`items[${itemIndex}][nature]`, nature);
    formData.append(`items[${itemIndex}][pi_label_no]`, pi);
    formData.append(`items[${itemIndex}][qc_sticker_no]`, qc);
    itemIndex += 1;
  });
  
  // Add images
  if (uploadedImages.length === 0) {
    throw new Error('At least one image is required');
  }
  uploadedImages.forEach((image, index) => {
    formData.append(`images[${index}]`, image);
  });
  
  // Add videos
  uploadedVideos.forEach((video, index) => {
    formData.append(`videos[${index}]`, video);
  });
  
  // Submit to API
  const response = await fetch(CLAIMS_API_URL, {
    method: 'POST',
    body: formData
  });

  const result = await response.json();

  if (result.success) {
    // Reset submit button
    const submitBtn = document.getElementById('submit-claim-btn');
    submitBtn.disabled = false;
    submitBtn.textContent = 'SUBMIT CLAIM';
    
    // Show success dialog
    showDialog(
      'Claim Submitted Successfully', 
      `Your claim has been submitted successfully.<br><strong>Claim Number:</strong> ${result.data.claim_number || result.data.claim_id}`,
      'success'
    );
    
    // Close form and refresh claims list
    setTimeout(() => {
      hideClaimForm();
      resetClaimForm();
      loadClaims();
    }, 2000);
  } else {
    throw new Error(result.message || 'Failed to submit claim');
  }
}

/**
 * Show custom dialog (replaces alerts)
 */
function showDialog(title, message, type = 'info') {
  // Remove existing dialog if any
  const existing = document.getElementById('custom-dialog');
  if (existing) existing.remove();
  
  const dialog = document.createElement('div');
  dialog.id = 'custom-dialog';
  dialog.className = 'claim-modal';
  dialog.style.display = 'flex';
  
  const iconMap = {
    'success': '✓',
    'error': '✕',
    'info': 'ℹ'
  };
  
  const colorMap = {
    'success': '#4CAF50',
    'error': '#f44336',
    'info': '#2196F3'
  };
  
  dialog.innerHTML = `
    <div class="claim-modal-overlay" onclick="closeDialog()"></div>
    <div class="claim-modal-content" style="max-width: 500px;">
      <div class="claim-modal-header">
        <h2 style="display: flex; align-items: center; gap: 10px;">
          <span style="display: inline-flex; align-items: center; justify-content: center; width: 40px; height: 40px; border-radius: 50%; background: ${colorMap[type]}; color: white; font-size: 24px; font-weight: bold;">${iconMap[type]}</span>
          ${title}
        </h2>
        <button class="claim-modal-close" onclick="closeDialog()">&times;</button>
      </div>
      <div class="claim-modal-body">
        <p style="margin: 0; line-height: 1.6;">${message}</p>
      </div>
      <div class="claim-modal-footer" style="display: flex; justify-content: flex-end; padding: 1rem; border-top: 1px solid #e0e0e0;">
        <button onclick="closeDialog()" class="button button--primary" style="min-width: 100px;">OK</button>
      </div>
    </div>
  `;
  
  document.body.appendChild(dialog);
}

function closeDialog() {
  const dialog = document.getElementById('custom-dialog');
  if (dialog) dialog.remove();
}

/**
 * Show success modal
 */
function showSuccessModal(data) {
  const modal = document.getElementById('success-modal');
  const claimNumber = data.claim_number || data.id;
  
  document.getElementById('success-claim-number').textContent = claimNumber;
  modal.style.display = 'flex';
  
  // Reset submit button
  const submitBtn = document.getElementById('submit-claim-btn');
  submitBtn.disabled = false;
  submitBtn.textContent = 'SUBMIT CLAIM';
}

/**
 * Close success modal
 */
function closeSuccessModal() {
  document.getElementById('success-modal').style.display = 'none';
}

/**
 * View claims after success
 */
function viewClaims() {
  closeSuccessModal();
  hideClaimForm();
  loadClaims();
}

/**
 * Show claim details
 */
async function showClaimDetails(claimId) {
  const modal = document.getElementById('claim-details-modal');
  const loadingEl = document.getElementById('claim-details-loading');
  const contentEl = document.getElementById('claim-details-content');
  const errorEl = document.getElementById('claim-details-error');
  
  modal.style.display = 'flex';
  window.currentClaimId = claimId; // Store globally for update function
  loadingEl.style.display = 'block';
  contentEl.style.display = 'none';
  errorEl.style.display = 'none';
  
  try {
    const response = await fetch(`${CLAIMS_API_URL}/${claimId}`, {
      headers: {
        'X-Store': STORE_IDENTIFIER
      }
    });
    
    if (!response.ok) {
      throw new Error('Failed to fetch claim details');
    }
    
    const result = await response.json();
    
    if (result.success && result.data) {
      displayClaimDetails(result.data);
      loadingEl.style.display = 'none';
      contentEl.style.display = 'block';
    } else {
      throw new Error('Invalid response');
    }
  } catch (error) {
    console.error('Error loading claim details:', error);
    loadingEl.style.display = 'none';
    errorEl.style.display = 'block';
  }
}

/**
 * Display claim details in modal
 */
function displayClaimDetails(claim) {
  const contentEl = document.getElementById('claim-details-content');
  
  // ========================================
  // HIDDEN FEATURES: Media Editing & PI Label Editing
  // 
  // TO RESTORE:
  // 1. Change finalAllowedRole from false to true (or implement proper role checking)
  // 2. Original role check was:
  //    const isAllowedRole = customerData && customerData.tags && 
  //      (customerData.tags.includes('Dev') || customerData.tags.includes('dev') ||
  //       customerData.tags.includes('Admin') || customerData.tags.includes('admin') ||
  //       customerData.tags.includes('Operations') || customerData.tags.includes('operations'));
  //    const finalAllowedRole = isAllowedRole;
  // 
  // This will restore: Add/Delete Images, Add/Delete Videos, PI Label inline editing
  // ========================================
  const isAllowedRole = false;
  const finalAllowedRole = false;
  
  // Format products with better details
  let productsHtml = '<div class="claim-products-list">';
  if (claim.items && claim.items.length > 0) {
    claim.items.forEach((item, index) => {
      const explicitTitle = item.title || item.product_title || item.product_name || item.name;
      const productName = getProductTitle(item.product_id, item.sku, explicitTitle);
      productsHtml += `
        <div class="claim-product-item">
          <div class="product-item-header">
            <strong>${productName} || SKU: ${item.sku || 'N/A'}</strong>
            <span class="product-quantity">Qty: ${item.quantity}</span>
          </div>
          <div class="product-item-details">
            <span><strong>Nature:</strong> ${item.nature || 'N/A'}</span>
            <span><strong>PI Label:</strong> ${item.pi_label_no || item.pi_label || 'N/A'}</span>
            ${item.qc_sticker_no ? `<span><strong>QC Sticker:</strong> ${item.qc_sticker_no}</span>` : ''}
            ${item.supplier ? `<span><strong>Supplier:</strong> ${item.supplier}</span>` : ''}
          </div>
        </div>
      `;
    });
  }
  productsHtml += '</div>';
  
  // Format images with proper URL and admin controls
  let imagesHtml = '';
  if (claim.images && claim.images.length > 0 || finalAllowedRole) {
    imagesHtml = '<div class="claim-images-grid">';
    if (claim.images && claim.images.length > 0) {
      claim.images.forEach((image, index) => {
        const imageUrl = image.url.startsWith('http') ? image.url : `https://app.roveconcepts.com${image.url}`;
        imagesHtml += `
          <div class="media-item">
            <img src="${imageUrl}" alt="${image.file_name}" class="claim-image-thumb" onclick="window.open('${imageUrl}', '_blank')" title="${image.file_name}">
            ${finalAllowedRole ? `<button class="media-delete-btn" onclick="deleteMediaItem('image', ${index})" title="Delete">×</button>` : ''}
          </div>
        `;
      });
    }
    if (finalAllowedRole) {
      imagesHtml += `
        <div class="media-item media-add">
          <label for="add-images-input" class="media-add-label">
            <span>+</span>
            <small>Add Images</small>
          </label>
          <input type="file" id="add-images-input" accept="image/*" multiple onchange="handleAddImages(event)" style="display: none;">
        </div>
      `;
    }
    imagesHtml += '</div>';
  }
  
  // Order display: name (e.g. "#1001") from customer orders, fallback to order_id
  const detailOrderDisplay = (window.customerOrders && claim.order_id)
    ? (window.customerOrders.find(o => o.id == claim.order_id)?.name || claim.order_id)
    : (claim.order_id || 'N/A');
  
  // Format videos with admin controls
  let videosHtml = '';
  if (claim.videos && claim.videos.length > 0 || finalAllowedRole) {
    videosHtml = '<div class="claim-videos-grid">';
    if (claim.videos && claim.videos.length > 0) {
      claim.videos.forEach((video, index) => {
        const videoUrl = video.url.startsWith('http') ? video.url : `https://app.roveconcepts.com${video.url}`;
        videosHtml += `
          <div class="media-item">
            <video src="${videoUrl}" controls class="claim-video-thumb" title="${video.file_name}"></video>
            ${finalAllowedRole ? `<button class="media-delete-btn" onclick="deleteMediaItem('video', ${index})" title="Delete">×</button>` : ''}
          </div>
        `;
      });
    }
    if (finalAllowedRole) {
      videosHtml += `
        <div class="media-item media-add">
          <label for="add-videos-input" class="media-add-label">
            <span>+</span>
            <small>Add Videos</small>
          </label>
          <input type="file" id="add-videos-input" accept="video/*" multiple onchange="handleAddVideos(event)" style="display: none;">
        </div>
      `;
    }
    videosHtml += '</div>';
  }
  
  contentEl.innerHTML = `
    <div class="claim-detail-row">
      <div class="claim-detail-label">Claim Number</div>
      <div class="claim-detail-value"><strong>${claim.claim_number}</strong></div>
    </div>
    
    <div class="claim-detail-row">
      <div class="claim-detail-label">Order</div>
      <div class="claim-detail-value">${detailOrderDisplay}</div>
    </div>
    
    <div class="claim-detail-row">
      <div class="claim-detail-label">Status</div>
      <div class="claim-detail-value">${getStatusBadge(claim.status)}</div>
    </div>
    
    <div class="claim-detail-row">
      <div class="claim-detail-label">Created</div>
      <div class="claim-detail-value">${formatDate(claim.created_at)}</div>
    </div>
    
    <div class="claim-detail-row">
      <div class="claim-detail-label">Last Updated</div>
      <div class="claim-detail-value">${formatDate(claim.updated_at)}</div>
    </div>
    
    <div class="claim-detail-row">
      <div class="claim-detail-label">Customer</div>
      <div class="claim-detail-value">
        ${claim.customer_name}<br>
        <small>${claim.customer_email}</small>
      </div>
    </div>
    
    <div class="claim-detail-row">
      <div class="claim-detail-label">Products (${claim.items_count})</div>
      <div class="claim-detail-value">${productsHtml}</div>
    </div>
    
    <div class="claim-detail-row">
      <div class="claim-detail-label">Issue Description</div>
      <div class="claim-detail-value">${claim.issue_description || 'N/A'}</div>
    </div>
    
    <div class="claim-detail-row">
      <div class="claim-detail-label">Ideal Resolution</div>
      <div class="claim-detail-value">${claim.ideal_resolution || 'N/A'}</div>
    </div>
    
    <div class="claim-detail-row">
      <div class="claim-detail-label">Customer Request</div>
      <div class="claim-detail-value">${claim.customer_request || 'N/A'}</div>
    </div>
    
    ${imagesHtml ? `
    <div class="claim-detail-row">
      <div class="claim-detail-label">Images (${claim.images_count})</div>
      <div class="claim-detail-value">${imagesHtml}</div>
    </div>
    ` : ''}
    
    ${videosHtml ? `
    <div class="claim-detail-row">
      <div class="claim-detail-label">Videos (${claim.videos_count})</div>
      <div class="claim-detail-value">${videosHtml}</div>
    </div>
    ` : ''}
    
    <div class="claim-detail-row">
      <a class="button button--primary" href="/pages/contact">
        Contact Support
      </a>
    </div>
  `;

  // <div class="claim-detail-row">
  //     <button class="button button--primary" onclick="window.location.href='mailto:hello@roveconcepts.com?subject=Claim%20${claim.claim_number}'">
  //       Contact Support
  //     </button>
  //   </div>
  
  // ========================================
  // HIDDEN: Admin management section disabled
  // TO RESTORE: Uncomment the line below AND uncomment the HTML section in main-claims.liquid
  // ========================================
  // showAdminManagementSection(claim);
}/**
 * Show and populate admin management section
 */
function showAdminManagementSection(claim) {
  const adminSection = document.getElementById('admin-management-section');
  console.log('Admin Section Element:', adminSection);
  console.log('Claim Data:', claim);
  
  if (!adminSection) {
    console.error('Admin section not found in DOM!');
    return;
  }
  
  // Check if user has allowed role (Dev, Admin, or Operations) - case insensitive
  const isAllowedRole = customerData && customerData.tags && 
    (customerData.tags.includes('Dev') || customerData.tags.includes('dev') ||
     customerData.tags.includes('Admin') || customerData.tags.includes('admin') ||
     customerData.tags.includes('Operations') || customerData.tags.includes('operations'));
  
  // DEBUG: Log customer data and role check
  console.log('Customer Data:', customerData);
  console.log('Customer Tags:', customerData?.tags);
  console.log('Is Allowed Role:', isAllowedRole);
  
  // TEMPORARY: Force enable for testing - REMOVE THIS LATER
  const forceEnable = true;
  const finalRole = isAllowedRole || forceEnable;
  console.log('Final Role (with force):', finalRole);
  
  // Show admin section to all users
  adminSection.style.display = 'block';
  console.log('Admin section display set to block');
  
  // Get all editable fields
  const editableFields = adminSection.querySelectorAll('.admin-editable, input[name="claim_status"]');
  
  if (finalRole) {
    // Admin/Dev/Operations: Enable all fields
    editableFields.forEach(field => {
      field.disabled = false;
      if (field.type === 'radio') {
        field.parentElement.style.cursor = 'pointer';
      }
    });
  } else {
    // Regular customers: Disable all editable fields (read-only)
    editableFields.forEach(field => {
      field.disabled = true;
      if (field.type === 'radio') {
        field.parentElement.style.cursor = 'not-allowed';
      }
    });
  }
  
  // Populate checklist product name
  const checklistProduct = document.getElementById('checklist-product-name');
  if (checklistProduct && claim.items && claim.items.length > 0) {
    const firstItem = claim.items[0];
    const defectType = firstItem.defect_type || firstItem.nature_of_issue || firstItem.nature || 'Issue';
    const explicitTitle = firstItem.title || firstItem.product_title || firstItem.product_name || firstItem.name;
    const productTitle = getProductTitle(firstItem.product_id, firstItem.sku, explicitTitle);
    checklistProduct.textContent = `${productTitle} (${defectType})`;
  }
  
  // Populate checklist dropdown
  const checklist = document.getElementById('detail-checklist');
  if (checklist) {
    checklist.value = claim.checklist || '';
  }
  
  // Populate resolution fields
  const initialRes = document.getElementById('detail-initial-resolution');
  const secondRes = document.getElementById('detail-second-resolution');
  const finalRes = document.getElementById('detail-final-resolution');
  const freshdeskRef = document.getElementById('detail-freshdesk-reference');
  const claimNotes = document.getElementById('detail-claim-notes');
  
  if (initialRes) initialRes.value = claim.initial_resolution || '';
  if (secondRes) secondRes.value = claim.second_resolution || '';
  if (finalRes) finalRes.value = claim.final_resolution || '';
  if (freshdeskRef) freshdeskRef.value = claim.freshdesk_reference || '';
  if (claimNotes) claimNotes.value = claim.claim_notes || '';
  
  // Set status radio button
  const status = claim.status || 'Open';
  const statusRadio = document.querySelector(`input[name="claim_status"][value="${status}"]`);
  if (statusRadio) {
    statusRadio.checked = true;
  }
  
  // Populate tables
  populateReplacementsTable(claim);
  populateRefundsTable(claim);
  populateAuditReasonTable(claim);
}

/**
 * Get product title from customer orders by product_id
 */
function getProductTitle(productId, sku, explicitTitle) {
  if (explicitTitle) return explicitTitle;

  // Try to find product in customer orders
  if (customerOrders && customerOrders.length) {
    for (const order of customerOrders) {
      const lineItem = order.line_items.find(item => item.product_id == productId);
      if (lineItem && lineItem.title) {
        return lineItem.title;
      }
    }
  }
  // Fallback: avoid showing SKU as the product name
  return 'N/A';
}

/**
 * Populate Replacements Table
 */
function populateReplacementsTable(claim) {
  console.log('🔧 populateReplacementsTable called');
  console.log('Claim items:', claim.items);
  const container = document.getElementById('replacements-table-container');
  console.log('Container element:', container);
  if (!container) {
    console.error('❌ Replacements container not found!');
    return;
  }
  
  // Check if user has allowed role (Dev, Admin, or Operations) - case insensitive
  const isAdmin = customerData && customerData.tags && 
    (customerData.tags.includes('Dev') || customerData.tags.includes('dev') ||
     customerData.tags.includes('Admin') || customerData.tags.includes('admin') ||
     customerData.tags.includes('Operations') || customerData.tags.includes('operations'));
  
  // TEMPORARY: Force enable for testing - REMOVE THIS LATER
  const forceEnable = true;
  const finalAdmin = isAdmin || forceEnable;
  
  const items = claim.items || [];
  
  console.log('Replacements Table - Claim items:', items);
  if (items.length > 0) {
    console.log('First item fields:', Object.keys(items[0]));
    console.log('First item data:', items[0]);
  }
  
  if (items.length === 0) {
    container.innerHTML = '<p>No products in this claim</p>';
    return;
  }
  
  let tableHtml = `
    <table class="claim-admin-table">
      <thead>
        <tr>
          <th>Product</th>
          <th>SKU</th>
          <th>Qty</th>
          <th>Replace?</th>
          <th>Reason</th>
        </tr>
      </thead>
      <tbody>
  `;
  
  const defectSubCategories = [
    'Assembly / Fit Issue',
    'Cloudy Finish',
    'Consistent Shipping Damage',
    'Discoloration',
    'Failure After Normal Use',
    'Finish Quality Poor',
    'Feather Loss',
    'Gapping',
    'Labeling Error',
    'Mismatched Across Order / Within Same Order',
    'Missing or Incorrect Parts',
    'Missing or Misaligned Holes',
    'Not as Advertised',
    'Packaging Damage',
    'Premature Degradation',
    'Sagging',
    'Sinking',
    'Stitching Issues',
    'Stripped Threading',
    'Uneven Finish / Surface',
    'Visible Glue',
    'Warping',
    'Wobbly / Unstable'
  ];
  
  items.forEach((item, index) => {
    const defectType = item.defect_type || '';
    const explicitTitle = item.title || item.product_title || item.product_name || item.name;
    const productTitle = getProductTitle(item.product_id, item.sku, explicitTitle);
    const reasonOptions = defectSubCategories.map(d => 
      `<option value="${d}" ${defectType === d ? 'selected' : ''}>${d}</option>`
    ).join('');
    
    tableHtml += `
      <tr>
        <td>${productTitle}</td>
        <td>${item.sku || '-'}</td>
        <td>${item.quantity}</td>
        <td>
          ${finalAdmin ? `
            <select class="admin-table-input" data-type="replacement" data-index="${index}" data-field="replace">
              <option value="No" ${(item.replace === 'No' || !item.replace) ? 'selected' : ''}>No</option>
              <option value="Yes" ${item.replace === 'Yes' ? 'selected' : ''}>Yes</option>
            </select>
          ` : (item.replace || 'No')}
        </td>
        <td>
          ${finalAdmin ? `
            <select class="admin-table-input" data-type="replacement" data-index="${index}" data-field="reason">
              <option value="">-- Select --</option>
              ${reasonOptions}
            </select>
          ` : (item.replacement_reason || '-')}
        </td>
      </tr>
    `;
  });
  
  tableHtml += '</tbody></table>';
  container.innerHTML = tableHtml;
}

/**
 * Populate Refunds Table
 */
function populateRefundsTable(claim) {
  console.log('💰 populateRefundsTable called');
  console.log('Claim items:', claim.items);
  const container = document.getElementById('refunds-table-container');
  console.log('Refunds container:', container);
  if (!container) {
    console.error('❌ Refunds container not found!');
    return;
  }
  
  // Check if user has allowed role (Dev, Admin, or Operations) - case insensitive
  const isAdmin = customerData && customerData.tags && 
    (customerData.tags.includes('Dev') || customerData.tags.includes('dev') ||
     customerData.tags.includes('Admin') || customerData.tags.includes('admin') ||
     customerData.tags.includes('Operations') || customerData.tags.includes('operations'));
  
  // TEMPORARY: Force enable for testing - REMOVE THIS LATER
  const forceEnable = true;
  const finalAdmin = isAdmin || forceEnable;
  
  const items = claim.items || [];
  
  if (items.length === 0) {
    container.innerHTML = '<p>No products in this claim</p>';
    return;
  }
  
  const defectSubCategories = [
    'Assembly / Fit Issue',
    'Cloudy Finish',
    'Consistent Shipping Damage',
    'Discoloration',
    'Failure After Normal Use',
    'Finish Quality Poor',
    'Feather Loss',
    'Gapping',
    'Labeling Error',
    'Mismatched Across Order / Within Same Order',
    'Missing or Incorrect Parts',
    'Missing or Misaligned Holes',
    'Not as Advertised',
    'Packaging Damage',
    'Premature Degradation',
    'Sagging',
    'Sinking',
    'Stitching Issues',
    'Stripped Threading',
    'Uneven Finish / Surface',
    'Visible Glue',
    'Warping',
    'Wobbly / Unstable'
  ];
  
  let tableHtml = `
    <table class="claim-admin-table">
      <thead>
        <tr>
          <th>Product</th>
          <th>SKU</th>
          <th>Qty</th>
          <th>Refund Amt</th>
          <th>Type</th>
          <th>Reason</th>
        </tr>
      </thead>
      <tbody>
  `;
  
  items.forEach((item, index) => {
    const defectType = item.defect_type || '';
    const explicitTitle = item.title || item.product_title || item.product_name || item.name;
    const productTitle = getProductTitle(item.product_id, item.sku, explicitTitle);
    const reasonOptions = defectSubCategories.map(d => 
      `<option value="${d}" ${defectType === d ? 'selected' : ''}>${d}</option>`
    ).join('');
    
    tableHtml += `
      <tr>
        <td>${productTitle}</td>
        <td>${item.sku || '-'}</td>
        <td>${item.quantity}</td>
        <td>
          ${finalAdmin ? `
            <input type="number" step="0.01" class="admin-table-input" data-type="refund" data-index="${index}" data-field="amount" value="${item.refund_amount || '0.00'}" placeholder="0.00">
          ` : (item.refund_amount || '0.00')}
        </td>
        <td>
          ${finalAdmin ? `
            <select class="admin-table-input" data-type="refund" data-index="${index}" data-field="type">
              <option value="Regular" ${(item.refund_type === 'Regular' || !item.refund_type) ? 'selected' : ''}>Regular</option>
              <option value="Partial" ${item.refund_type === 'Partial' ? 'selected' : ''}>Partial</option>
              <option value="Full" ${item.refund_type === 'Full' ? 'selected' : ''}>Full</option>
            </select>
          ` : (item.refund_type || 'Regular')}
        </td>
        <td>
          ${finalAdmin ? `
            <select class="admin-table-input" data-type="refund" data-index="${index}" data-field="reason">
              <option value="">-- Select --</option>
              ${reasonOptions}
            </select>
          ` : (item.refund_reason || '-')}
        </td>
      </tr>
    `;
  });
  
  tableHtml += '</tbody></table>';
  container.innerHTML = tableHtml;
}

/**
 * Populate Audit Reason Table
 */
function populateAuditReasonTable(claim) {
  console.log('📋 populateAuditReasonTable called');
  console.log('Claim items:', claim.items);
  const container = document.getElementById('audit-reason-table-container');
  console.log('Audit container:', container);
  if (!container) {
    console.error('❌ Audit Reason container not found!');
    return;
  }
  
  // Check if user has allowed role (Dev, Admin, or Operations) - case insensitive
  const isAdmin = customerData && customerData.tags && 
    (customerData.tags.includes('Dev') || customerData.tags.includes('dev') ||
     customerData.tags.includes('Admin') || customerData.tags.includes('admin') ||
     customerData.tags.includes('Operations') || customerData.tags.includes('operations'));
  
  // TEMPORARY: Force enable for testing - REMOVE THIS LATER
  const forceEnable = true;
  const finalAdmin = isAdmin || forceEnable;
  
  const items = claim.items || [];
  
  if (items.length === 0) {
    container.innerHTML = '<p>No products in this claim</p>';
    return;
  }
  
  const defectSubCategories = [
    'Assembly / Fit Issue',
    'Cloudy Finish',
    'Consistent Shipping Damage',
    'Discoloration',
    'Failure After Normal Use',
    'Finish Quality Poor',
    'Feather Loss',
    'Gapping',
    'Labeling Error',
    'Mismatched Across Order / Within Same Order',
    'Missing or Incorrect Parts',
    'Missing or Misaligned Holes',
    'Not as Advertised',
    'Packaging Damage',
    'Premature Degradation',
    'Sagging',
    'Sinking',
    'Stitching Issues',
    'Stripped Threading',
    'Uneven Finish / Surface',
    'Visible Glue',
    'Warping',
    'Wobbly / Unstable'
  ];
  
  let tableHtml = `
    <table class="claim-admin-table">
      <thead>
        <tr>
          <th>Product</th>
          <th>SKU</th>
          <th>Reason</th>
        </tr>
      </thead>
      <tbody>
  `;
  
  items.forEach((item, index) => {
    const defectType = item.defect_type || '';
    const explicitTitle = item.title || item.product_title || item.product_name || item.name;
    const productTitle = getProductTitle(item.product_id, item.sku, explicitTitle);
    const reasonOptions = defectSubCategories.map(d => 
      `<option value="${d}" ${defectType === d ? 'selected' : ''}>${d}</option>`
    ).join('');
    
    tableHtml += `
      <tr>
        <td>${productTitle}</td>
        <td>${item.sku || '-'}</td>
        <td>
          ${finalAdmin ? `
            <select class="admin-table-input" data-type="audit" data-index="${index}" data-field="reason">
              <option value="">-- Select --</option>
              ${reasonOptions}
            </select>
          ` : (item.audit_reason || '-')}
        </td>
      </tr>
    `;
  });
  
  tableHtml += '</tbody></table>';
  container.innerHTML = tableHtml;
}

/**
 * Save admin management data
 */
async function saveAdminManagement() {
  // Role restriction removed - anyone can save audit changes
  
  // Collect all data
  const claimId = document.getElementById('claim-modal-title').textContent.split('#')[1]?.trim();
  
  if (!claimId) {
    alert('Unable to determine claim ID');
    return;
  }
  
  // Get selected status from radio buttons
  const statusRadio = document.querySelector('input[name="claim_status"]:checked');
  
  const data = {
    checklist: document.getElementById('detail-checklist')?.value || '',
    initial_resolution: document.getElementById('detail-initial-resolution')?.value || '',
    second_resolution: document.getElementById('detail-second-resolution')?.value || '',
    final_resolution: document.getElementById('detail-final-resolution')?.value || '',
    freshdesk_reference: document.getElementById('detail-freshdesk-reference')?.value || '',
    claim_notes: document.getElementById('detail-claim-notes')?.value || '',
    status: statusRadio ? statusRadio.value : 'Open',
    replacements: [],
    refunds: [],
    audit_reasons: []
  };
  
  // Collect replacement data
  document.querySelectorAll('[data-type="replacement"]').forEach(input => {
    const index = input.dataset.index;
    const field = input.dataset.field;
    if (!data.replacements[index]) data.replacements[index] = {};
    data.replacements[index][field] = input.value;
  });
  
  // Collect refund data
  document.querySelectorAll('[data-type="refund"]').forEach(input => {
    const index = input.dataset.index;
    const field = input.dataset.field;
    if (!data.refunds[index]) data.refunds[index] = {};
    data.refunds[index][field] = input.value;
  });
  
  // Collect audit reasons
  document.querySelectorAll('[data-type="audit"]').forEach(input => {
    const index = input.dataset.index;
    const field = input.dataset.field;
    if (!data.audit_reasons[index]) data.audit_reasons[index] = {};
    data.audit_reasons[index][field] = input.value;
  });
  
  try {
    console.log('Saving admin data for claim:', claimId);
    console.log('Data to save:', data);
    
    const response = await fetch(`${CLAIMS_API_URL}/${claimId}/admin`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'X-Store': getStoreIdentifier()
      },
      body: JSON.stringify(data)
    });
    
    console.log('Response status:', response.status);
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('Server error:', errorText);
      throw new Error(`Server returned ${response.status}: ${errorText}`);
    }
    
    const result = await response.json();
    console.log('Save result:', result);
    
    if (result.success) {
      alert('✅ Claim admin data saved successfully!');
      // Optionally reload the claim details to show updated data
      // loadClaimDetails(claimId);
    } else {
      throw new Error(result.message || 'Failed to save');
    }
  } catch (error) {
    console.error('Error saving admin data:', error);
    alert(`❌ Error saving changes: ${error.message}\n\nCheck browser console for details.`);
  }
}

/**
 * Handle adding new images to claim
 */
window.claimImagesToAdd = [];
window.claimVideosToAdd = [];
window.mediaToDelete = { images: [], videos: [] };

function handleAddImages(event) {
  const files = Array.from(event.target.files);
  
  // Validate files
  for (const file of files) {
    if (file.size > 10 * 1024 * 1024) {
      alert(`Image ${file.name} is too large. Maximum size is 10MB.`);
      continue;
    }
    if (!file.type.match('image/(jpeg|jpg|png)')) {
      alert(`File ${file.name} is not a valid image. Only JPG and PNG are allowed.`);
      continue;
    }
    window.claimImagesToAdd.push(file);
  }
  
  if (window.claimImagesToAdd.length > 0) {
    updateAddMediaPreview();
    showDialog('Images Selected', `${files.length} image(s) selected. Click UPDATE CLAIM to save.`, 'success');
  }
  event.target.value = '';
}

/**
 * Handle adding new videos to claim
 */
function handleAddVideos(event) {
  const files = Array.from(event.target.files);
  
  // Validate files
  for (const file of files) {
    if (file.size > 50 * 1024 * 1024) {
      alert(`Video ${file.name} is too large. Maximum size is 50MB.`);
      continue;
    }
    if (!file.type.match('video/(mp4|mov|avi)')) {
      alert(`File ${file.name} is not a valid video. Only MP4, MOV, and AVI are allowed.`);
      continue;
    }
    window.claimVideosToAdd.push(file);
  }
  
  if (window.claimVideosToAdd.length > 0) {
    updateAddMediaPreview();
    showDialog('Videos Selected', `${files.length} video(s) selected. Click UPDATE CLAIM to save.`, 'success');
  }
  event.target.value = '';
}

/**
 * Update preview to show pending media additions
 */
function updateAddMediaPreview() {
  // Find add buttons and update their labels
  const addImagesLabel = document.querySelector('label[for="add-images-input"]');
  const addVideosLabel = document.querySelector('label[for="add-videos-input"]');
  
  if (addImagesLabel) {
    const small = addImagesLabel.querySelector('small');
    if (window.claimImagesToAdd.length > 0) {
      small.textContent = `Add Images (${window.claimImagesToAdd.length} pending)`;
      addImagesLabel.classList.add('pending-uploads');
    } else {
      small.textContent = 'Add Images';
      addImagesLabel.classList.remove('pending-uploads');
    }
  }
  
  if (addVideosLabel) {
    const small = addVideosLabel.querySelector('small');
    if (window.claimVideosToAdd.length > 0) {
      small.textContent = `Add Videos (${window.claimVideosToAdd.length} pending)`;
      addVideosLabel.classList.add('pending-uploads');
    } else {
      small.textContent = 'Add Videos';
      addVideosLabel.classList.remove('pending-uploads');
    }
  }
}

/**
 * Mark media item for deletion
 */
function deleteMediaItem(type, index) {
  if (!confirm(`Mark this ${type} for deletion? Changes will be applied when you click UPDATE CLAIM.`)) return;
  
  // Store the index for deletion
  window.mediaToDelete[type + 's'].push(index);
  
  // Find the specific media item element
  const mediaSection = type === 'image' 
    ? document.querySelector('.claim-detail-row:has(.claim-images-grid)')
    : document.querySelector('.claim-detail-row:has(.claim-videos-grid)');
  
  if (mediaSection) {
    const mediaItems = mediaSection.querySelectorAll('.media-item:not(.media-add)');
    const targetItem = mediaItems[index];
    
    if (targetItem) {
      // Add visual indication
      targetItem.classList.add('pending-deletion');
      targetItem.style.opacity = '0.3';
      targetItem.style.pointerEvents = 'none';
      
      // Add deletion badge
      const badge = document.createElement('div');
      badge.className = 'deletion-badge';
      badge.textContent = 'Will be deleted';
      targetItem.appendChild(badge);
      
      // Disable the delete button
      const deleteBtn = targetItem.querySelector('.media-delete-btn');
      if (deleteBtn) {
        deleteBtn.disabled = true;
        deleteBtn.style.display = 'none';
      }
    }
  }
  
  showDialog('Marked for Deletion', `${type} marked for deletion. Click UPDATE CLAIM to save changes.`, 'info');
}

/**
 * Update claim details including PI labels, images, and videos
 */
async function updateClaimDetails() {
  // Check if user has allowed role
  const isAllowedRole = customerData && customerData.tags && 
    (customerData.tags.includes('Dev') || customerData.tags.includes('dev') ||
     customerData.tags.includes('Admin') || customerData.tags.includes('admin') ||
     customerData.tags.includes('Operations') || customerData.tags.includes('operations'));
  
  if (!isAllowedRole) {
    showDialog('Permission Denied', 'Only Dev, Admin, and Operations can update claim details.', 'error');
    return;
  }
  
  const claimId = window.currentClaimId;
  if (!claimId) {
    showDialog('Error', 'No claim selected', 'error');
    return;
  }
  
  // Disable the update button to prevent double submission
  const updateBtn = document.querySelector('button[onclick="updateClaimDetails()"]');
  if (updateBtn) {
    updateBtn.disabled = true;
    updateBtn.textContent = 'UPDATING...';
  }
  
  try {
    // Collect PI label updates
    const piLabels = [];
    document.querySelectorAll('[data-type="pi-label"]').forEach(input => {
      const index = parseInt(input.dataset.index);
      piLabels[index] = input.value;
    });
    
    // Prepare form data for file uploads
    const formData = new FormData();
    formData.append('_method', 'PATCH'); // Laravel method override
    
    // Add PI labels if any
    if (piLabels.length > 0) {
      formData.append('pi_labels', JSON.stringify(piLabels));
    }
    
    // Add new images
    if (window.claimImagesToAdd && window.claimImagesToAdd.length > 0) {
      console.log(`Adding ${window.claimImagesToAdd.length} new images`);
      window.claimImagesToAdd.forEach((file, index) => {
        formData.append(`images[${index}]`, file);
      });
    }
    
    // Add new videos
    if (window.claimVideosToAdd && window.claimVideosToAdd.length > 0) {
      console.log(`Adding ${window.claimVideosToAdd.length} new videos`);
      window.claimVideosToAdd.forEach((file, index) => {
        formData.append(`videos[${index}]`, file);
      });
    }
    
    // Add media deletion info
    if (window.mediaToDelete) {
      if (window.mediaToDelete.images && window.mediaToDelete.images.length > 0) {
        formData.append('delete_images', JSON.stringify(window.mediaToDelete.images));
        console.log(`Marking ${window.mediaToDelete.images.length} images for deletion`);
      }
      if (window.mediaToDelete.videos && window.mediaToDelete.videos.length > 0) {
        formData.append('delete_videos', JSON.stringify(window.mediaToDelete.videos));
        console.log(`Marking ${window.mediaToDelete.videos.length} videos for deletion`);
      }
    }
    
    console.log('Updating claim:', claimId);
    
    const response = await fetch(`${CLAIMS_API_URL}/${claimId}`, {
      method: 'POST', // Use POST with _method override for Laravel
      headers: {
        'X-Store': STORE_IDENTIFIER
      },
      body: formData
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('Server error:', errorText);
      throw new Error(`Server returned ${response.status}: ${errorText}`);
    }
    
    const result = await response.json();
    console.log('Update result:', result);
    
    if (result.success) {
      showDialog(
        'Claim Updated', 
        'Claim details, images, and videos have been updated successfully!', 
        'success'
      );
      
      // Clear pending changes
      window.claimImagesToAdd = [];
      window.claimVideosToAdd = [];
      window.mediaToDelete = { images: [], videos: [] };
      
      // Re-enable button
      if (updateBtn) {
        updateBtn.disabled = false;
        updateBtn.textContent = 'UPDATE CLAIM';
      }
      
      // Refresh claim details after short delay
      setTimeout(() => {
        closeClaimDetailsModal();
        showClaimDetails(claimId);
      }, 1500);
    } else {
      throw new Error(result.message || 'Failed to update claim');
    }
  } catch (error) {
    console.error('Error updating claim:', error);
    showDialog('Update Failed', `Error updating claim: ${error.message}`, 'error');
    
    // Re-enable button on error
    if (updateBtn) {
      updateBtn.disabled = false;
      updateBtn.textContent = 'UPDATE CLAIM';
    }
  }
}

/**
 * Close claim details modal
 */
function closeClaimDetailsModal() {
  const modal = document.getElementById('claim-details-modal');
  if (modal) {
    modal.style.display = 'none';
  }
  
  // Clear pending changes
  window.claimImagesToAdd = [];
  window.claimVideosToAdd = [];
  window.mediaToDelete = { images: [], videos: [] };
  window.currentClaimId = null;
  
  // Reset any visual indicators
  const deletionBadges = document.querySelectorAll('.deletion-badge');
  deletionBadges.forEach(badge => badge.remove());
  
  const mediaItems = document.querySelectorAll('.media-item');
  mediaItems.forEach(item => {
    item.style.opacity = '';
    item.style.pointerEvents = '';
  });
}

/**
 * Get status badge HTML
 */
function getStatusBadge(status) {
  const statusMap = {
    'pending': { label: 'Pending', class: 'status-pending' },
    'in_progress': { label: 'In Progress', class: 'status-in-progress' },
    'approved': { label: 'Approved', class: 'status-approved' },
    'rejected': { label: 'Rejected', class: 'status-rejected' },
    'completed': { label: 'Completed', class: 'status-completed' }
  };
  
  const statusInfo = statusMap[status] || { label: status, class: 'status-default' };
  return `<span class="status-badge ${statusInfo.class}">${statusInfo.label}</span>`;
}
